import { useState, useEffect, useCallback, SetStateAction } from "react";
import { notification } from "antd";
import { useMarkThreadAsRead } from "@lhd/hooks";
import {
  useUnreadThreadsList,
  ChatThreadDisplay,
} from "../UnreadThreadsList/hook";
import { useConfirmCloseChat } from "./useConfirmCloseChat";

export type ModalView = "list" | "chat" | "create";

export interface ModalState {
  id: string;
  view: ModalView;
  thread?: any;
  isDirty?: boolean;
  isFullscreen?: boolean;
}

export type ReceivedProps = {
  isOpen: boolean;
  isSubmitting?: boolean;
  onCancel: () => void;
  onOk: () => void;
  onRefetchReady?: (refetch: () => void) => void;
  initialThread?: any;
};

const FILE_TYPES = {
  image: "image/*",
  pdf: "application/pdf",
  doc: "application/msword",
  docx: "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
  xls: "application/vnd.ms-excel",
  xlsx: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  mp4: "video/mp4",
  mov: "video/quicktime",
  avi: "video/x-msvideo",
  mkv: "video/x-matroska",
};
export const ALLOWED_FILE_TYPES = Object.values(FILE_TYPES);
export const ALLOWED_MIME_TYPES = Object.values(FILE_TYPES).filter(
  (t) => t !== "image/*",
);

const useModalChat = (props: ReceivedProps) => {
  const { isOpen, onCancel, initialThread, onRefetchReady } = props;
  const [modals, setModals] = useState<ModalState[]>([]);
  const [expansionStack, setExpansionStack] = useState<string[]>([]);
  const [activeModalConfirmId, setActiveModalConfirmId] = useState<
    string | null
  >(null);
  const [confirmAction, setConfirmAction] = useState<"close" | "back" | null>(
    null,
  );

  const confirmCloseChat = useConfirmCloseChat();
  const { markAsRead } = useMarkThreadAsRead();

  const listProps = useUnreadThreadsList({
    isOpen,
    onRefetchReady,
  });

  const fullscreenIds = modals.filter((m) => m.isFullscreen).map((m) => m.id);

  // Manage modal slots initialization and deep links
  useEffect(() => {
    if (!isOpen) {
      setModals([]);
      setExpansionStack([]);
      return;
    }

    if (initialThread) {
      const threadIdStr = String(initialThread.id || "").replace("#", "");
      setModals((prev) => {
        const isAlreadyOpen = prev.some((m) => m.id === threadIdStr);
        if (isAlreadyOpen) return prev;

        const chatModal: ModalState = {
          id: threadIdStr,
          view: "chat" as const,
          thread: initialThread,
          isFullscreen: false,
        };

        if (prev.length === 0) {
          return [chatModal];
        } else if (prev.length === 1) {
          return [...prev, chatModal];
        } else {
          const next = [...prev];
          next[1] = chatModal;
          return next;
        }
      });
    } else if (modals.length === 0) {
      setModals([{ id: "initial-list", view: "list", isFullscreen: false }]);
    }
  }, [isOpen, initialThread]);

  const handleCloseModal = useCallback(
    (id: string) => {
      const modal = modals.find((m) => m.id === id);
      if (
        modal &&
        (modal.view === "create" || modal.view === "chat") &&
        modal.isDirty
      ) {
        setActiveModalConfirmId(id);
        setConfirmAction("close");
        confirmCloseChat.openConfirm();
        return;
      }

      setModals((prev) => {
        const next = prev.filter((m) => m.id !== id);
        if (next.length === 0) {
          onCancel();
        }
        return next;
      });

      setExpansionStack((prev) => prev.filter((fid) => fid !== id));
    },
    [modals, confirmCloseChat, onCancel],
  );

  const handleToggleFullscreen = (id: string, e: React.MouseEvent) => {
    e.stopPropagation();
    setModals((prev) =>
      prev.map((m) => {
        if (m.id === id) {
          const newState = !m.isFullscreen;
          if (newState) {
            setExpansionStack((stack) => [
              ...stack.filter((s) => s !== id),
              id,
            ]);
          } else {
            setExpansionStack((stack) => stack.filter((s) => s !== id));
          }
          return { ...m, isFullscreen: newState };
        }
        return m;
      }),
    );
  };

  const handleThreadClick = (
    thread: ChatThreadDisplay,
    sourceModalId: string,
  ) => {
    const threadIdStr = thread.id;

    setModals((prev) => {
      const isAlreadyOpen = prev.some((m) => m.id === threadIdStr);
      if (isAlreadyOpen) return prev;

      const sourceModal = prev.find((m) => m.id === sourceModalId);
      const shouldBeFullscreen = !!sourceModal?.isFullscreen;

      const chatModal: ModalState = {
        id: threadIdStr,
        view: "chat" as const,
        thread,
        isFullscreen: shouldBeFullscreen,
      };

      if (shouldBeFullscreen) {
        setExpansionStack((stack) => [
          ...stack.filter((s) => s !== threadIdStr),
          threadIdStr,
        ]);
      }

      if (prev.length === 1) {
        return [...prev, chatModal];
      } else {
        const oldId = prev[1].id;
        setExpansionStack((stack) => stack.filter((s) => s !== oldId));
        const next = [...prev];
        next[1] = chatModal;
        return next;
      }
    });
  };

  const handleChatCreated = (
    oldId: string,
    newThreadId: string,
    dataMessage: any,
  ) => {
    setModals((prev) =>
      prev.map((m) =>
        m.id === oldId
          ? {
              id: newThreadId,
              view: "chat" as const,
              thread: dataMessage,
              isFullscreen: m.isFullscreen,
            }
          : m,
      ),
    );

    setExpansionStack((stack) => {
      if (stack.includes(oldId)) {
        return [...stack.filter((s) => s !== oldId), newThreadId];
      }
      return stack;
    });

    if (dataMessage?.thread) {
      window.dispatchEvent(
        new CustomEvent("chatThreadCreated", {
          detail: { thread: dataMessage.thread },
        }),
      );
    }
  };

  const handleCreateChatClick = (modalId: string) => {
    setModals((prev) =>
      prev.map((m) => (m.id === modalId ? { ...m, view: "create" } : m)),
    );
  };

  const handleBackToList = (modalId: string) => {
    const modal = modals.find((m) => m.id === modalId);
    if (
      modal &&
      (modal.view === "create" || modal.view === "chat") &&
      modal.isDirty
    ) {
      setActiveModalConfirmId(modalId);
      setConfirmAction("back");
      confirmCloseChat.openConfirm();
      return;
    }

    setModals((prev) =>
      prev.map((m) => (m.id === modalId ? { ...m, view: "list" } : m)),
    );
  };

  const handleCopyLink = (threadId: string) => {
    const url = new URL(window.location.origin + window.location.pathname);
    url.searchParams.set("chat_thread_id", threadId);

    const link = url.toString();
    navigator.clipboard
      .writeText(link)
      .then(() => {
        notification.success({
          message: "リンクをコピーしました",
          placement: "topRight",
          duration: 2,
        });
      })
      .catch(() => {
        notification.error({
          message: "コピーに失敗しました",
        });
      });
  };

  const handleConfirmOk = () => {
    confirmCloseChat.handleOk(() => {
      if (activeModalConfirmId) {
        if (confirmAction === "close") {
          setModals((prev) => {
            const next = prev.filter((m) => m.id !== activeModalConfirmId);
            if (next.length === 0) onCancel();
            return next;
          });
          setExpansionStack((stack) =>
            stack.filter((s) => s !== activeModalConfirmId),
          );
        } else if (confirmAction === "back") {
          setModals((prev) =>
            prev.map((m) =>
              m.id === activeModalConfirmId ? { ...m, view: "list" } : m,
            ),
          );
        }
      }
      setActiveModalConfirmId(null);
      setConfirmAction(null);
    });
  };

  const handleConfirmCancel = () => {
    confirmCloseChat.handleCancel();
    setActiveModalConfirmId(null);
    setConfirmAction(null);
  };

  const activeModalConfirm = modals.find((m) => m.id === activeModalConfirmId);
  const confirmMessage =
    activeModalConfirm?.view === "chat"
      ? "未送信のメッセージがあります。閉じてもよろしいですか？"
      : "チャット作成中です。閉じてもよろしいですか？未送信の内容は失われます。";

  return {
    ...props,
    modals,
    setModals,
    fullscreenIds: expansionStack,
    confirmCloseChat,
    listProps,
    handleCloseModal,
    handleToggleFullscreen,
    handleThreadClick,
    handleCreateChatClick,
    handleChatCreated,
    handleBackToList,
    handleCopyLink,
    handleConfirmOk,
    handleConfirmCancel,
    confirmMessage,
  };
};

export type Props = ReturnType<typeof useModalChat>;
export default useModalChat;
